import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { GroupEnrollDialog, type GroupPaymentMethod } from "@/components/group-therapy/GroupEnrollDialog";
import { CircleNotch, CalendarDots, Users, Clock, Bank } from "@phosphor-icons/react";
import { apiGet, apiSend } from "@/lib/api-error";
import { formatCurrency, formatDate, formatTimeShort } from "@/lib/format";
import { toast } from "@/lib/toast";
import { submitMonriForm, type MonriFormParams } from "@/lib/monri";
import { useAuth } from "@/hooks/useAuth";

type CycleState = "open" | "locked" | "completed" | "cancelled";

const CLOSED_REASON: Record<CycleState, string> = {
  open: "Sva mjesta su popunjena.",
  locked: "Ciklus je već započeo. Prijave su zatvorene.",
  completed: "Ciklus je završen.",
  cancelled: "Ciklus je otkazan.",
};

export const Route = createFileRoute("/grupne-terapije/$id")({
  component: GroupTherapyDetail,
});

interface CycleDetail {
  cycle: {
    id: string;
    name: string;
    description: string;
    price: number;
    maxParticipants: number;
    durationMinutes: number;
    state: CycleState;
    remainingSeats: number;
    therapist: {
      id: string;
      name: string;
      profileImageUrl: string | null;
      bio: string;
    };
  };
  sessions: Array<{ id: string; scheduledAt: string; cancelledAt: string | null }>;
}

interface EnrollResponse {
  enrollment: { id: string };
  paymentMethod: "monri" | "bank_transfer";
  monri?: MonriFormParams;
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    recipientName: string;
    reference: string;
    amount: number;
    currency: string;
  };
}

function GroupTherapyDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<GroupPaymentMethod>("monri");
  const [bankResult, setBankResult] = useState<EnrollResponse["bankDetails"] | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public", "group-therapy", id],
    queryFn: () =>
      apiGet<CycleDetail>(`/api/group-therapies/${id}`, "Greška pri učitavanju"),
  });

  const enrollMutation = useMutation({
    mutationFn: () =>
      apiSend<EnrollResponse>(
        `/api/group-therapies/${id}/enroll`,
        "POST",
        { paymentMethod },
        "Greška pri prijavi"
      ),
    onSuccess: (resp) => {
      if (resp.paymentMethod === "monri" && resp.monri) {
        submitMonriForm(resp.monri);
      } else if (resp.bankDetails) {
        setBankResult(resp.bankDetails);
        setEnrollOpen(false);
        // The seat is now held: refresh the remaining-seat count.
        void queryClient.invalidateQueries({ queryKey: ["public", "group-therapy", id] });
        toast.success("Prijava primljena. Slijedite uputstva za uplatu.");
      }
    },
    onError: (err: Error) => {
      if (/unauthorized/i.test(err.message)) {
        redirectToLogin();
        return;
      }
      toast.error(err.message);
    },
  });

  function redirectToLogin() {
    toast.error("Prijavite se da biste se prijavili na grupnu terapiju.");
    navigate({ to: "/login", search: { redirect: `/grupne-terapije/${id}` } });
  }

  function openEnroll() {
    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }
    setEnrollOpen(true);
  }

  // The API answers 404 for a cancelled cycle and for a deactivated
  // therapist's one; without this branch the page spun forever.
  if (isError && !data) {
    return (
      <div className="min-h-screen bg-[#fbf7f0]">
        <div className="mx-auto max-w-4xl px-4 py-12 text-center sm:px-6 lg:px-8">
          <p data-testid="gt-not-found" className="text-foreground">
            Ova grupna terapija nije dostupna. Moguće je da je otkazana.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => navigate({ to: "/grupne-terapije" })}>
            Sve grupne terapije
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  const { cycle, sessions } = data;
  const canEnroll = cycle.state === "open" && cycle.remainingSeats > 0;

  return (
    <div className="min-h-screen bg-[#fbf7f0]">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate({ to: "/grupne-terapije" })}
          className="mb-4"
        >
          ← Sve grupne terapije
        </Button>

        <div className="rounded-xl border border-[#ece8df] bg-white p-8">
          <div className="mb-4 flex items-center gap-3">
            {cycle.therapist.profileImageUrl ? (
              <img
                src={cycle.therapist.profileImageUrl}
                alt=""
                className="size-10 rounded-full object-cover"
              />
            ) : (
              <div className="size-10 rounded-full bg-[#ece8df]" />
            )}
            <div>
              <p className="text-sm font-medium text-foreground">{cycle.therapist.name}</p>
              <p className="text-xs text-muted-foreground">Stručno provjeren terapeut</p>
            </div>
          </div>

          <h1
            data-testid="gt-cycle-title"
            className="text-3xl font-bold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {cycle.name}
          </h1>
          <p className="mt-4 whitespace-pre-line text-foreground">{cycle.description}</p>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <InfoRow icon={<Users className="size-4" />} label="Slobodno" value={`${cycle.remainingSeats} / ${cycle.maxParticipants} mjesta`} />
            <InfoRow icon={<Clock className="size-4" />} label="Trajanje sesije" value={`${cycle.durationMinutes} min`} />
            <InfoRow icon={<CalendarDots className="size-4" />} label="Broj sesija" value={String(sessions.filter((s) => !s.cancelledAt).length)} />
            <InfoRow label="Cijena ciklusa" value={formatCurrency(cycle.price)} />
          </div>

          <div className="mt-6 rounded-lg border border-[#ece8df] p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Termini</h3>
            <ol className="space-y-1 text-sm">
              {sessions.map((s, i) => (
                <li key={s.id} className="flex items-center gap-2 text-foreground">
                  <span className="w-6 text-muted-foreground">{i + 1}.</span>
                  {formatDate(s.scheduledAt)} u {formatTimeShort(s.scheduledAt)}
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-6">
            {canEnroll ? (
              <Button data-testid="gt-enroll-open" size="lg" className="w-full" onClick={openEnroll}>
                Prijavi se · {formatCurrency(cycle.price)}
              </Button>
            ) : (
              <p data-testid="gt-closed-reason" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">
                {CLOSED_REASON[cycle.state]}
              </p>
            )}
          </div>
        </div>

        {bankResult && (
          <div data-testid="gt-bank-details" className="mt-6 rounded-xl border border-[#ece8df] bg-white p-6">
            <h3 className="mb-3 flex items-center gap-2 text-base font-semibold">
              <Bank className="size-5" /> Uputstva za uplatu
            </h3>
            <dl className="space-y-1 text-sm">
              <DetailRow label="Banka" value={bankResult.bankName} />
              <DetailRow label="Račun" value={bankResult.accountNumber} />
              <DetailRow label="Primalac" value={bankResult.recipientName} />
              <DetailRow label="Poziv na broj" value={bankResult.reference} />
              <DetailRow label="Iznos" value={formatCurrency(bankResult.amount)} />
            </dl>
          </div>
        )}
      </div>

      <GroupEnrollDialog
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        cycleId={cycle.id}
        listPrice={cycle.price}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        onConfirm={() => enrollMutation.mutate()}
        isPending={enrollMutation.isPending}
      />
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-[#ece8df] p-3 text-sm">
      {icon}
      <span className="text-muted-foreground">{label}:</span>
      <span className="ml-auto font-medium text-foreground">{value}</span>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value}</dd>
    </div>
  );
}
